<template>
  <div class="va-favourites-menu d-inline-flex" data-testid="variance-analysis-favourites-menu">
    <v-menu
      v-model="isMenuOpen"
      :close-on-content-click="false"
      offset-y
      transition="scale-transition"
      min-width="300"
      max-width="300"
    >
      <template v-slot:activator="{ on, attrs }">
        <base-button
          class="favourites-btn"
          secondary
          v-bind="attrs"
          v-on="on"
          label="Favourites"
          data-testid="variance-analysis-filter__favourites-button"
        />
      </template>
      <v-card class="favourites-menu__card" data-testid="variance-analysis-favourites-menu__card">
        <v-card-title class="favourites-menu__header popupHeader pa-3">
          <div class="d-flex align-center gap-2">
            <span class="font-weight-medium" data-testid="variance-analysis-favourites-menu__heading">
              {{ favouriteDialogTitle }}
            </span>
            <v-tooltip bottom content-class="tooltip-bottom">
              <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                <v-icon
                  class="white--text"
                  v-bind="tooltipAttrs"
                  v-on="tooltipOn"
                  data-testid="variance-analysis-favourites-menu__info-icon"
                >
                  mdi-information-outline
                </v-icon>
              </template>
              <span>{{ favouriteInfoText }}</span>
            </v-tooltip>
          </div>
          <v-btn
            icon
            @click="closeMenu"
            class="white--text"
            data-testid="variance-analysis-favourites-menu__close-button"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-3">
          <v-text-field
            v-model="favouriteTitle"
            :label="favouriteTitleLabel"
            :placeholder="favouriteTitlePlaceholder"
            outlined
            dense
            hide-details="auto"
            data-testid="variance-analysis-favourites-menu__title-input"
          />

          <!-- Scrollable middle: confirm warning + section header + list -->
          <div class="favourites-menu__scrollable">
            <div
              v-if="showReplaceConfirm"
              class="favourites-menu__confirm-replace mt-2"
              data-testid="variance-analysis-favourites-menu__confirm-replace"
            >
              <div class="favourites-menu__confirm-message">
                <span>{{ confirmReplaceMessage }} <strong>"{{ oldestFilterTitle }}"</strong></span>
              </div>
            </div>

            <div
              class="favourites-menu__section-header mt-4 mb-2"
              data-testid="variance-analysis-favourites-menu__recent-label"
            >
              <h5 class="font-weight-medium">{{ favouriteRecentSectionTitle }}</h5>
              <v-progress-circular
                v-if="isFavouriteListLoading"
                indeterminate
                size="18"
                width="2"
                color="primary"
                data-testid="variance-analysis-favourites-menu__loading-indicator"
              />
            </div>

            <v-list
              dense
              class="favourites-menu__list pa-0 d-flex flex-column gap-2"
              data-testid="variance-analysis-favourites-menu__list"
            >
              <template v-if="!isFavouriteListLoading && !favouriteFilters.length">
                <v-list-item data-testid="variance-analysis-favourites-menu__empty">
                  <v-list-item-content>
                    <v-list-item-title>{{ favouriteEmptyStateText }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </template>
              <template v-else>
                <v-list-item
                  v-for="(filter, idx) in favouriteFilters"
                  :key="filter._id || filter.id"
                  :class="['favourites-menu__item pa-1', { 'favourites-menu__item--disabled': isFavouriteDeleting && favouriteDeleteTargetId === (filter._id || filter.id) }]"
                  data-testid="variance-analysis-favourites-menu__item"
                  @click="handleSelectFavourite(filter)"
                >
                  <v-list-item-content class="pa-0 d-grid gap-1">
                    <v-list-item-title class="font-weight-medium d-flex align-center gap-2">
                      <div class="serial-circle">
                        {{ idx + 1 }}
                      </div>
                      <h6 class="font-weight-medium mb-0">{{ filter.title || favouriteUntitledLabel }}</h6>
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action class="ma-0">
                    <v-btn
                      icon
                      small
                      :loading="isFavouriteDeleting && favouriteDeleteTargetId === (filter._id || filter.id)"
                      :disabled="isFavouriteDeleting && favouriteDeleteTargetId !== (filter._id || filter.id)"
                      @click.stop="handleRemoveFavourite(filter)"
                      data-testid="variance-analysis-favourites-menu__delete-button"
                    >
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
              </template>
            </v-list>
          </div>

          <!-- Buttons: Cancel+Continue&Save when confirming, plain Save otherwise -->
          <div class="d-flex justify-end gap-2 mt-2">
            <template v-if="showReplaceConfirm">
              <v-btn
                class="text-transform-none secondaryBtn"
                :disabled="isFavouriteSaving"
                @click="cancelReplaceConfirm"
                data-testid="variance-analysis-favourites-menu__confirm-cancel-btn"
              >
                {{ confirmCancelCtaText }}
              </v-btn>
              <v-btn
                class="text-transform-none primary"
                :loading="isFavouriteSaving"
                @click="confirmSaveFavourite"
                data-testid="variance-analysis-favourites-menu__confirm-save-btn"
              >
                {{ confirmSaveCtaText }}
              </v-btn>
            </template>
            <template v-else>
              <v-btn
                class="text-transform-none primary"
                @click="handleSaveFavourite"
                :loading="isFavouriteSaving"
                :disabled="isFavouriteSaveDisabled"
                data-testid="variance-analysis-favourites-menu__save-button"
              >
                {{ favouriteSaveCtaText }}
              </v-btn>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </v-menu>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { FAVOURITE_FILTERS } from '../../../utils/constants';

export default {
  name: 'VarianceAnalysisFavouriteFiltersMenu',
  props: {
    favouriteContext: {
      type: Object,
      default: () => ({}),
    },
    lookupMaps: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isMenuOpen: false,
      favouriteTitle: '',
      favouriteFormError: '',
      favouriteFiltersLoaded: false,
      showReplaceConfirm: false,
    };
  },
  computed: {
    ...mapGetters('varianceanalysis/favouriteFilters', {
      favouriteFilters: 'favouriteFilters',
      isFavouriteListLoading: 'isFavouriteFiltersLoading',
      isFavouriteSaving: 'isFavouriteFiltersSaving',
      isFavouriteDeleting: 'isFavouriteFiltersDeleting',
      favouriteDeleteTargetId: 'favouriteFilterDeleteTarget',
    }),
    favouriteInfoText() {
      return FAVOURITE_FILTERS.UI.INFO_TEXT;
    },
    favouriteEmptyStateText() {
      return FAVOURITE_FILTERS.UI.EMPTY_STATE;
    },
    favouriteDialogTitle() {
      return FAVOURITE_FILTERS.UI.DIALOG_TITLE;
    },
    favouriteTitleLabel() {
      return FAVOURITE_FILTERS.UI.TITLE_LABEL;
    },
    favouriteTitlePlaceholder() {
      return FAVOURITE_FILTERS.UI.TITLE_PLACEHOLDER;
    },
    favouriteSaveCtaText() {
      return FAVOURITE_FILTERS.UI.SAVE_CTA;
    },
    favouriteRecentSectionTitle() {
      return FAVOURITE_FILTERS.UI.RECENT_SECTION_TITLE;
    },
    favouriteUntitledLabel() {
      return FAVOURITE_FILTERS.UI.UNTITLED_LABEL;
    },
    isFavouriteSaveDisabled() {
      return !this.favouriteTitle.trim() || this.isFavouriteSaving;
    },
    isAtLimit() {
      return this.favouriteFilters.length >= FAVOURITE_FILTERS.MAX_LIMIT;
    },
    oldestFilterTitle() {
      if (!this.favouriteFilters.length) return '';
      return this.favouriteFilters[this.favouriteFilters.length - 1]?.title || FAVOURITE_FILTERS.UI.UNTITLED_LABEL;
    },
    confirmReplaceMessage() {
      return FAVOURITE_FILTERS.UI.CONFIRM_REPLACE_MESSAGE;
    },
    confirmCancelCtaText() {
      return FAVOURITE_FILTERS.UI.CONFIRM_CANCEL_CTA;
    },
    confirmSaveCtaText() {
      return FAVOURITE_FILTERS.UI.CONFIRM_SAVE_CTA;
    },
  },
  watch: {
    isMenuOpen(isOpen) {
      if (isOpen) {
        this.onMenuOpened();
      } else {
        this.resetFormState();
      }
    },
  },
  methods: {
    ...mapActions('varianceanalysis/favouriteFilters', {
      fetchFavouriteFiltersAction: 'fetchFavouriteFilters',
      createFavouriteFilterAction: 'createFavouriteFilter',
      deleteFavouriteFilterAction: 'deleteFavouriteFilter',
    }),
    handleSelectFavourite(filter) {
      const filterId = filter?._id || filter?.id;
      if (!filter || (this.isFavouriteDeleting && this.favouriteDeleteTargetId === filterId)) {
        return;
      }
      this.$emit('select-favourite', filter);
      this.closeMenu();
    },
    async onMenuOpened() {
      this.favouriteFormError = '';
      if (!this.favouriteFiltersLoaded) {
        await this.loadFavouriteFilters(false);
      }
    },
    async loadFavouriteFilters(notify = false) {
      if (this.isFavouriteListLoading) {
        return;
      }
      try {
        await this.fetchFavouriteFiltersAction({ axiosInstance: this.axios, notify });
        this.favouriteFiltersLoaded = true;
      } catch (error) {
        this.favouriteFormError = error?.message || FAVOURITE_FILTERS.MESSAGES.FETCH_ERROR;
      }
    },
    async handleSaveFavourite() {
      this.favouriteFormError = '';
      const trimmedTitle = this.favouriteTitle.trim();
      if (!trimmedTitle) {
        this.favouriteFormError = FAVOURITE_FILTERS.MESSAGES.TITLE_REQUIRED;
        return;
      }
      if (this.isAtLimit) {
        this.showReplaceConfirm = true;
        return;
      }
      await this.doSaveFavourite(trimmedTitle);
    },
    async confirmSaveFavourite() {
      this.showReplaceConfirm = false;
      const trimmedTitle = this.favouriteTitle.trim();
      await this.doSaveFavourite(trimmedTitle);
    },
    cancelReplaceConfirm() {
      this.showReplaceConfirm = false;
    },
    async doSaveFavourite(trimmedTitle) {
      const payload = this.buildFavouritePayload(trimmedTitle);
      try {
        await this.createFavouriteFilterAction({ axiosInstance: this.axios, payload });
        this.favouriteTitle = '';
        if (!this.favouriteFiltersLoaded) {
          this.favouriteFiltersLoaded = true;
        }
      } catch (error) {
        this.favouriteFormError = error?.message || FAVOURITE_FILTERS.MESSAGES.CREATE_ERROR;
      }
    },
    buildFavouritePayload(title) {
      const {
        cycletype = '',
        selectedperiod = '',
        selectedregion = '',
        selectedcountry = '',
        selectednetwork = '',
        selectedreportingunit = '',
        selectedReportingUnitType = 'all',
        selectedyear = '',
      } = this.favouriteContext || {};
      return {
        title,
        period: selectedperiod,
        region: selectedregion,
        country: selectedcountry,
        network: selectednetwork,
        reportingunit: selectedreportingunit,
        type: selectedReportingUnitType || 'all',
        cycletype,
        year: selectedyear,
      };
    },
    async handleRemoveFavourite(filter) {
      const filterId = filter?._id || filter?.id;
      if (!filterId) {
        return;
      }
      try {
        await this.deleteFavouriteFilterAction({ axiosInstance: this.axios, filterId });
      } catch (error) {
        this.favouriteFormError = error?.message || FAVOURITE_FILTERS.MESSAGES.DELETE_ERROR;
      }
    },
    filterSummary(filter) {
      if (!filter || typeof filter !== 'object') {
        return FAVOURITE_FILTERS.UI.SUMMARY_FALLBACK;
      }
      const summaryParts = [];
      const summaryFields = FAVOURITE_FILTERS.UI.SUMMARY_FIELDS || [];
      summaryFields.forEach(({ key, label }) => {
        if (!key || !label) {
          return;
        }
        const rawValue = filter[key];
        if (rawValue === undefined || rawValue === null || rawValue === '') {
          return;
        }
        const resolvedValue = this.resolveFavouriteValueLabel(key, rawValue);
        if (resolvedValue) {
          summaryParts.push(`${label}: ${resolvedValue}`);
        }
      });
      if (!summaryParts.length) {
        return FAVOURITE_FILTERS.UI.SUMMARY_FALLBACK;
      }
      return summaryParts.join(FAVOURITE_FILTERS.UI.SUMMARY_SEPARATOR || ' • ');
    },
    resolveFavouriteValueLabel(fieldKey, value) {
      const mapping = this.lookupMaps || {};
      const periodMap = mapping.periodlist || {};
      const networkMap = mapping.networklist || {};
      const regionMap = mapping.regionlist || {};
      const countryMap = mapping.countrylist || {};
      const reportingUnitMap = mapping.reportingunitlist || {};
      const typeMap = mapping.reportingUnitTypeList || {};
      const normalizedValue = typeof value === 'object' && value !== null
        ? value._id || value.id || value.value || value.code || value.name || ''
        : value;
      if (normalizedValue === 'all' || normalizedValue === '' || normalizedValue === null || normalizedValue === undefined) {
        return FAVOURITE_FILTERS.UI.VALUE_ALL_LABEL;
      }
      if (fieldKey === 'type') {
        return typeMap?.[normalizedValue]?.name || value?.name || normalizedValue;
      }
      if (fieldKey === 'year') {
        return normalizedValue;
      }
      const lookupMap = {
        period: periodMap,
        network: networkMap,
        region: regionMap,
        country: countryMap,
        reportingunit: reportingUnitMap,
      };
      const resolved = lookupMap[fieldKey]?.[normalizedValue];
      if (resolved && resolved.name) {
        return resolved.name;
      }
      if (typeof value === 'object' && value?.name) {
        return value.name;
      }
      return normalizedValue;
    },
    closeMenu() {
      this.isMenuOpen = false;
    },
    resetFormState() {
      this.favouriteTitle = '';
      this.favouriteFormError = '';
      this.showReplaceConfirm = false;
    },
    resetLocalState() {
      this.resetFormState();
      this.isMenuOpen = false;
      this.favouriteFiltersLoaded = false;
    },
  },
  beforeDestroy() {
    this.resetLocalState();
  },
};
</script>

<style scoped>
.va-favourites-menu {
  position: relative;
}

.favourites-menu__card {
  min-width: 300px;
  max-width: 300px;
}

.favourites-menu__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.favourites-menu__section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.favourites-menu__list {
  /* height managed by .favourites-menu__scrollable */
}

.favourites-menu__item + .favourites-menu__item {
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}
.favourites-menu__item {
  cursor: pointer;
}
.favourites-menu__item--disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
/* Serial number circle */
.serial-circle {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1.5px solid #9aa4ad;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: #2c3e50;
  flex-shrink: 0;
}
/* Scrollable middle section — fixed height so confirm panel never grows the card */
.favourites-menu__scrollable {
  max-height: 300px;
  overflow-y: auto;
}
/* Replace confirmation panel */
.favourites-menu__confirm-replace {
  background: #fff8e1;
  border: 1px solid #ffe082;
  border-radius: 6px;
  padding: 10px 12px 8px;
}
.favourites-menu__confirm-message {
  display: flex;
  align-items: flex-start;
  font-size: 0.8rem;
  line-height: 1.5;
  color: #5d4037;
  gap: 4px;
}
</style>
